import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { ArrowPathIcon, CameraIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '../lib/utils';

function buildAuth() {
  const token = localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}` } };
}

function qrSrc(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const t = raw.trim().replace(/\s/g, '');
  if (!t) return null;
  if (t.startsWith('data:') || t.startsWith('http')) return t;
  return `data:image/png;base64,${t}`;
}

function WaCameraScanner({ regionId, enabled, onDecoded }) {
  const onDecodedRef = useRef(onDecoded);
  const lastText = useRef('');
  const html5Ref = useRef(null);
  onDecodedRef.current = onDecoded;

  useEffect(() => {
    lastText.current = '';
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !regionId) return undefined;

    const html5 = new Html5Qrcode(regionId, false);
    html5Ref.current = html5;
    const config = { fps: 8, qrbox: { width: 220, height: 220 } };

    const onOk = (decodedText) => {
      if (!decodedText || decodedText === lastText.current) return;
      lastText.current = decodedText;
      onDecodedRef.current(decodedText);
    };

    const startCam = async () => {
      try {
        await html5.start({ facingMode: 'environment' }, config, onOk, () => {});
      } catch {
        try {
          await html5.start({ facingMode: 'user' }, config, onOk, () => {});
        } catch (e2) {
          toast.error(
            e2.message ||
              'Não foi possível usar a câmera. Confirme permissão do navegador e use HTTPS (ou localhost).'
          );
        }
      }
    };

    void startCam();

    return () => {
      const h = html5Ref.current;
      html5Ref.current = null;
      if (h) {
        h.stop()
          .then(() => h.clear())
          .catch(() => {});
      }
    };
  }, [enabled, regionId]);

  if (!enabled) return null;

  return (
    <div className="mt-3 space-y-2">
      <div
        id={regionId}
        className="mx-auto min-h-[240px] w-full max-w-sm overflow-hidden rounded-lg bg-muted ring-1 ring-border"
      />
    </div>
  );
}

export default function WhatsAppConnectionModal({
  open,
  instanceId,
  uazapiToken,
  instanceName,
  initialQr,
  initialPairing,
  baseUrl,
  onClose,
  onConnected,
  onDisconnected
}) {
  const [localQr, setLocalQr] = useState(null);
  const [localPairing, setLocalPairing] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const onConnectedRef = useRef(onConnected);
  const onCloseRef = useRef(onClose);
  const onDisconnectedRef = useRef(onDisconnected);
  onConnectedRef.current = onConnected;
  onCloseRef.current = onClose;
  onDisconnectedRef.current = onDisconnected;

  const useTokenApi = Boolean(!instanceId && uazapiToken);
  const regionId = useMemo(
    () => `wa-h5qr-${String(instanceId || uazapiToken || 'x').replace(/[^a-z0-9-]/gi, '')}`,
    [instanceId, uazapiToken]
  );

  const lastDecodeToast = useRef(0);
  const handleDecoded = useCallback((text) => {
    const now = Date.now();
    if (now - lastDecodeToast.current < 2200) return;
    lastDecodeToast.current = now;
    if (text && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(text).then(
        () => toast.success('QR lido e texto copiado.', { duration: 3500 }),
        () => toast.success('QR lido pela câmera.', { duration: 3500 })
      );
    } else {
      toast.success('QR lido pela câmera.', { duration: 3500 });
    }
  }, []);

  const refreshConnect = useCallback(async () => {
    if (!instanceId && !uazapiToken) return;
    setConnectLoading(true);
    try {
      const body = {
        systemName: instanceName?.trim() || 'Siriusys',
        browser: 'auto'
      };
      const res = useTokenApi
        ? await axios.post(
            `${baseUrl}/api/whatsapp-instances/uazapi-token/connect`,
            { token: uazapiToken, ...body },
            { ...buildAuth(), timeout: 90000 }
          )
        : await axios.post(
            `${baseUrl}/api/whatsapp-instances/${instanceId}/connect`,
            body,
            { ...buildAuth(), timeout: 90000 }
          );
      setLocalQr(res.data.connectionQrCode ?? null);
      setLocalPairing(res.data.connectionPairingCode ?? null);
      if (res.data.status === 'connected') {
        toast.success('WhatsApp conectado.');
        onConnectedRef.current?.();
        onCloseRef.current?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Falha ao atualizar QR Code');
    } finally {
      setConnectLoading(false);
    }
  }, [baseUrl, instanceId, uazapiToken, useTokenApi, instanceName]);

  const disconnect = useCallback(async () => {
    if (!instanceId && !uazapiToken) return;
    if (!window.confirm('Desconectar este WhatsApp da instância?')) return;
    setConnectLoading(true);
    try {
      if (useTokenApi) {
        await axios.post(
          `${baseUrl}/api/whatsapp-instances/uazapi-token/disconnect`,
          { token: uazapiToken },
          buildAuth()
        );
      } else {
        await axios.post(`${baseUrl}/api/whatsapp-instances/${instanceId}/disconnect`, {}, buildAuth());
      }
      toast.success('WhatsApp desconectado.');
      onDisconnectedRef.current?.();
      onCloseRef.current?.();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Falha ao desconectar.');
    } finally {
      setConnectLoading(false);
    }
  }, [baseUrl, instanceId, uazapiToken, useTokenApi]);

  const pollSync = useCallback(async () => {
    if (!instanceId && !uazapiToken) return;
    try {
      const res = useTokenApi
        ? await axios.get(`${baseUrl}/api/whatsapp-instances/uazapi-token/status`, {
            params: { token: uazapiToken },
            ...buildAuth()
          })
        : await axios.post(`${baseUrl}/api/whatsapp-instances/${instanceId}/sync`, {}, buildAuth());
      if (res.data?.status === 'connected') {
        toast.success('WhatsApp conectado.');
        onConnectedRef.current?.();
        onCloseRef.current?.();
      }
    } catch {
      /* silencioso: polling */
    }
  }, [baseUrl, instanceId, uazapiToken, useTokenApi]);

  useEffect(() => {
    if (!open) {
      setShowCamera(false);
      return;
    }
    setLocalQr(initialQr ?? null);
    setLocalPairing(initialPairing ?? null);
  }, [open, initialQr, initialPairing]);

  useEffect(() => {
    if (!open || (!instanceId && !uazapiToken)) return undefined;

    void refreshConnect();

    const poll = setInterval(() => void pollSync(), 2500);
    const qrRenew = setInterval(() => void refreshConnect(), 28000);

    return () => {
      clearInterval(poll);
      clearInterval(qrRenew);
    };
  }, [open, instanceId, uazapiToken, refreshConnect, pollSync]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && open) onCloseRef.current?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const src = qrSrc(localQr);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wa-conn-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-lg border border-border bg-card text-card-foreground shadow-lg">
        <button
          type="button"
          className="absolute right-3 top-3 z-10 rounded-md p-2 text-muted-foreground transition hover:bg-muted hover:text-foreground"
          onClick={() => onClose()}
          aria-label="Fechar"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="space-y-5 p-6 sm:p-8">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider text-primary">Pareamento</p>
            <h2 id="wa-conn-title" className="mt-1 pr-10 text-xl font-semibold tracking-tight text-card-foreground">
              Conectar WhatsApp
            </h2>
            {instanceName ? (
              <p className="mt-1 text-sm text-muted-foreground">
                Instância <span className="text-foreground">{instanceName}</span>
              </p>
            ) : null}
          </div>

          <ol className="list-decimal space-y-1.5 pl-4 text-sm leading-relaxed text-muted-foreground">
            <li>Abra o WhatsApp no celular.</li>
            <li>Menu (⋮) ou Ajustes → Aparelhos conectados → Conectar um aparelho.</li>
            <li>Aponte a câmera do telefone para o QR abaixo (ou use o código de pareamento).</li>
          </ol>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void refreshConnect()}
              disabled={connectLoading}
            >
              <ArrowPathIcon className={cn('h-4 w-4', connectLoading && 'animate-spin')} />
              Atualizar QR
            </Button>
            <Button
              type="button"
              variant={showCamera ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setShowCamera((v) => !v)}
            >
              <CameraIcon className="h-4 w-4" />
              {showCamera ? 'Ocultar câmera' : 'Escanear com a câmera'}
            </Button>
          </div>

          {showCamera ? (
            <div className="rounded-lg border border-border bg-muted/50 p-4">
              <p className="text-xs leading-relaxed text-muted-foreground">
                Câmera deste dispositivo para ler outros QRs. Para vincular o WhatsApp, o celular deve escanear o{' '}
                <strong className="text-foreground">QR grande</strong> abaixo.
              </p>
              <WaCameraScanner regionId={regionId} enabled={showCamera} onDecoded={handleDecoded} />
            </div>
          ) : null}

          <div className="space-y-2">
            <Label className="text-muted-foreground">QR Code da instância</Label>
            {src ? (
              <div className="flex justify-center rounded-lg border border-border bg-background p-4 ring-1 ring-border/60">
                <img src={src} alt="QR Code para conectar WhatsApp" className="h-64 w-64 max-w-full object-contain" />
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border bg-muted/30 px-6 py-12 text-center">
                <p className="text-sm text-muted-foreground">
                  {connectLoading ? 'Gerando QR…' : 'Nenhum QR disponível. Clique em Atualizar QR.'}
                </p>
              </div>
            )}
          </div>

          {localPairing ? (
            <div className="rounded-lg border border-border bg-muted/40 px-4 py-3">
              <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Código de pareamento
              </p>
              <code className="mt-1 block font-mono text-lg tracking-widest text-foreground">{localPairing}</code>
            </div>
          ) : null}

          <p className="text-xs leading-relaxed text-muted-foreground">
            O status é verificado automaticamente. Ao conectar, esta janela fecha.
          </p>

          <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-5">
            <Button type="button" variant="outline" onClick={() => void disconnect()} disabled={connectLoading}>
              Desconectar
            </Button>
            <Button type="button" variant="outline" onClick={() => onClose()}>
              Fechar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
